import React, { useEffect, useState } from 'react';
import PageHeader from './components/PageHeader';
import { DoorbaseTile, DoorbaseTileGrid } from './components/DoorbaseTile';
import { apiGet, apiPut } from './utils/api';

function ukWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function EngineerAppOffice() {
  const [flags, setFlags] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [checks, setChecks] = useState([]);
  const [forms, setForms] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState('');

  async function load() {
    const [work, settings, vans, powra, flagsForOffice] = await Promise.all([
      apiGet('/api/mobile/v2/further-work'),
      apiGet('/api/mobile/v2/settings/tiles'),
      apiGet('/api/mobile/v2/office/vehicle-checks'),
      apiGet('/api/mobile/v2/office/powra'),
      apiGet('/api/mobile/v2/office/alerts'),
    ]);
    setFlags(work.flags || []);
    setTiles(settings.tiles || []);
    setChecks(vans.checks || []);
    setForms(powra.forms || []);
    setAlerts(flagsForOffice.alerts || []);
  }

  useEffect(() => {
    load().catch((err) => setError(err.message || 'Could not load'));
  }, []);

  async function toggle(tile) {
    setError('');
    try {
      const saved = await apiPut('/api/mobile/v2/settings/tiles', {
        tiles: { [tile.key]: !tile.enabled },
      });
      setTiles(saved.tiles || []);
    } catch (err) {
      setError(err.message || 'Could not save');
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Engineer app"
        subtitle="Further work, vehicle checks, POWRA forms, and which tiles engineers see"
        backTo="/ops"
        backLabel="Operations"
        sticky
      />
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {error ? <p className="text-red-700">{error}</p> : null}
        <section className="bg-white border-2 border-black rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Further work needed</h2>
          {flags.length === 0 ? <p className="text-gray-600">None waiting.</p> : null}
          <ul className="space-y-3">
            {flags.map((flag) => (
              <li key={flag.id} className="border border-gray-300 rounded p-3">
                <p className="font-semibold">{flag.jobNumber} · {flag.engineer || 'Engineer'}</p>
                <p>{flag.note}</p>
                <p className="text-sm text-gray-500">{flag.photoCount} photo{flag.photoCount === 1 ? '' : 's'}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-white border-2 border-black rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Start of day alerts</h2>
          {alerts.length === 0 ? <p className="text-gray-600">None waiting.</p> : null}
          <ul className="space-y-3">
            {alerts.map((alert) => (
              <li key={alert.id} className="border border-gray-300 rounded p-3">
                <p className="font-semibold">{alert.engineer || 'Engineer'} · {alert.kind}</p>
                <p>{alert.message}</p>
                <p className="text-sm text-gray-500">{alert.jobNumber ? `Job No: ${alert.jobNumber} · ` : ''}{ukWhen(alert.createdAt)}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-white border-2 border-black rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Vehicle checks</h2>
          {checks.length === 0 ? <p className="text-gray-600">None recorded.</p> : null}
          <ul className="space-y-3">
            {checks.map((check) => (
              <li key={check.id} className="border border-gray-300 rounded p-3">
                <p className="font-semibold">{check.engineer || 'Engineer'} · {check.registration}</p>
                <p>Mileage: {check.mileage} · {check.failed ? 'Fail' : 'Pass'}</p>
                <p className="text-sm text-gray-500">{ukWhen(check.completedAt)}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-white border-2 border-black rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">POWRA</h2>
          {forms.length === 0 ? <p className="text-gray-600">None recorded.</p> : null}
          <ul className="space-y-3">
            {forms.map((form) => (
              <li key={form.id} className="border border-gray-300 rounded p-3">
                <p className="font-semibold">Job No: {form.jobNumber} · {form.engineer || 'Engineer'}</p>
                <p>Decision: {form.decision}</p>
                <p className="text-sm text-gray-500">{ukWhen(form.completedAt)}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="bg-gray-300 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Engineer tiles</h2>
          <DoorbaseTileGrid columns={3}>
            {tiles.filter((tile) => tile.built).map((tile) => (
              <DoorbaseTile
                key={tile.key}
                icon={tile.icon}
                label={tile.label}
                description={tile.enabled ? 'Shown on the phone' : 'Hidden on the phone'}
                onClick={() => toggle(tile)}
              />
            ))}
          </DoorbaseTileGrid>
          <p className="text-sm text-gray-700 mt-4">Tap a tile to show or hide it. Later tiles appear here when they are built.</p>
        </section>
      </main>
    </div>
  );
}

export default EngineerAppOffice;
